import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { getCheckoutStatus, getBillingProfile } from "~/lib/network/billing";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";

export default function PaymentStatus() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"success" | "failed" | "loading">(
    "loading"
  );
  const [upgradedPlan, setUpgradedPlan] = useState<string | null>(null);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      try {
        // Fetch checkout status
        const checkoutStatus = await getCheckoutStatus();

        if (checkoutStatus.payment_status === "succeeded") {
          // Payment succeeded - fetch billing profile to get upgraded plan
          const profile = await getBillingProfile();
          setUpgradedPlan(
            profile.billing_plan.charAt(0).toUpperCase() +
              profile.billing_plan.slice(1)
          );
          setStatus("success");
        } else {
          // Payment failed, expired, or pending
          setStatus("failed");
        }
      } catch (err) {
        console.error("Failed to fetch payment status:", err);
        setStatus("failed");
      }
    };

    fetchPaymentStatus();
  }, []);

  // Auto-redirect to /session after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/session");
    }, 4000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {status === "loading" ? (
          <div className="card bg-base-200 shadow-lg p-4">
            <div className="card-body items-center text-center">
              {/* Loading Icon Skeleton */}
              <div className="skeleton w-25 h-25 rounded-full mb-4"></div>

              {/* Loading Title Skeleton */}
              <div className="skeleton h-10 w-48 mb-4"></div>

              {/* Loading Message Skeleton */}
              <div className="skeleton h-6 w-full mb-2"></div>
              <div className="skeleton h-6 w-3/4 mb-6"></div>
            </div>
          </div>
        ) : status === "success" ? (
          <div className="card bg-base-200 shadow-lg p-4">
            <div className="card-body items-center text-center">
              {/* Success Icon */}
              <div className="w-25 h-25 rounded-full bg-success/10 flex items-center justify-center mb-4">
                <IoCheckmarkCircle size={70} className="text-success" />
              </div>

              {/* Success Title */}
              <h2 className="card-title text-3xl font-bold text-success mb-2">
                Success
              </h2>

              {/* Success Message */}
              <p className="text-base-content/70 mb-6 text-lg">
                Your plan has been successfully upgraded to{" "}
                <span className="font-semibold text-success">
                  {upgradedPlan}
                </span>
                . You now have access to all premium features.
              </p>
            </div>
          </div>
        ) : (
          <div className="card bg-base-200 shadow-lg p-4">
            <div className="card-body items-center text-center">
              {/* Error Icon */}
              <div className="w-25 h-25 rounded-full bg-error/10 flex items-center justify-center mb-4">
                <IoCloseCircle size={70} className="text-error" />
              </div>

              {/* Error Title */}
              <h2 className="card-title text-2xl font-bold text-error mb-2">
                Error
              </h2>

              {/* Error Message */}
              <p className="text-base-content/70 mb-6 text-lg">
                Payment processing failed or was cancelled. Please try again or
                contact support if the issue persists.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
